import { Leaf, Phone, ShieldCheck, Users } from 'lucide-react';
import { Link } from 'react-router';

import { Reveal } from '@/components/public/Reveal';
import { LocalBusinessJsonLd, Seo } from '@/components/public/Seo';
import {
  Card,
  Lede,
  Photo,
  Section,
  SectionHeading,
  SectionIntro,
} from '@/components/public/Section';
import { WorkCarousel } from '@/components/public/WorkCarousel';
import { Button } from '@/components/ui/button';
import {
  AWARDS_HOME_ORDER,
  FEATURED_AWARD,
  CREW_PHOTO,
  HERO_PHOTO,
  HOME,
  HOME_HOW_IT_WORKS,
  NAP,
  awardAlt,
} from '@/lib/public-content';

/*
 * The home page — ROADMAP.md §8.5.
 *
 * Hero, What We Do, Why Choose Honeycomb, awards, crew, work carousel, CTA — in
 * that order, which is the live site's. **Every string comes from
 * `@/lib/public-content`, ported from the capture.** Nothing on this page was
 * written by the rebuild.
 *
 * **The alignment rule** (owner feedback, 2026-08-04): a heading is aligned the
 * way the thing it introduces is aligned. What We Do, Why Choose, Awards and
 * Our Work all introduce symmetrical rows, so their headings are centred — a
 * left-flush heading over three evenly-spaced cards leaves a dead corner. The
 * hero and the crew section are split with a photograph and stay left, because
 * that is prose somebody reads rather than a label over a grid.
 */

export function HomePage() {
  return (
    <>
      <Seo title={HOME.title} description={HOME.description} path="/" />
      {/* Once, on this page only — three copies would leave a search engine
          guessing which describes the business. */}
      <LocalBusinessJsonLd />

      <Hero />
      <WhatWeDo />
      <WhyChoose />
      <Awards />
      {/*
        **Here rather than higher up, for two reasons.** It reads as the answer
        to a question the page has just earned the right to be asked — what we
        do, why we are safe, proof we are good, *so what actually happens if I
        ask?* — and it hands straight over to the Crew block, whose captured
        copy opens "Our process is about transparency". Higher up it would be a
        third grid of cards in a row.
      */}
      <HowItWorks />
      <Crew />
      <OurWork />
      <ClosingCta />
    </>
  );
}

function Hero() {
  return (
    <section aria-labelledby="hero-heading" className="relative overflow-hidden bg-ink-50">
      {/*
        A soft honey wash behind the hero, and nothing anywhere else.
        design-system.md keeps the palette flat on purpose; one gradient at the
        top of the site gives the page somewhere to start without turning the
        brand into a colour scheme. `aria-hidden` and `pointer-events-none`
        because it is texture, not content.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-honey-100/70 via-ink-50 to-ink-50"
      />

      <div className="relative mx-auto grid max-w-content items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-honey-300 bg-white/80 px-3 py-1.5 text-[12px] font-semibold text-bark-700">
              <span aria-hidden className="size-1.5 rounded-full bg-honey-500" />
              Best of Iron County — four years running
            </p>
          </Reveal>

          <Reveal delay={60}>
            <h1
              id="hero-heading"
              className="mt-5 text-balance text-[38px] font-bold leading-[1.08] tracking-tight sm:text-[48px] lg:text-[56px]"
            >
              {HOME.hero.heading}
            </h1>
          </Reveal>

          <Reveal delay={120}>
            <p className="mt-5 max-w-xl text-[18px] leading-relaxed text-ink-700">
              {HOME.hero.body}
            </p>
          </Reveal>

          <Reveal delay={180}>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="shadow-card transition-shadow hover:shadow-pop">
                <Link to="/contact#quote">{HOME.hero.cta}</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href={NAP.phoneHref}>
                  <Phone aria-hidden />
                  {NAP.phone}
                </a>
              </Button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={120}>
          <Photo src={HERO_PHOTO.src} alt={HERO_PHOTO.alt} priority imageClassName="aspect-[4/3]" />
        </Reveal>
      </div>
    </section>
  );
}

function WhatWeDo() {
  return (
    <Section labelledBy="what-we-do">
      <SectionIntro
        id="what-we-do"
        heading={HOME.whatWeDo.heading}
        lede={HOME.whatWeDo.sub}
        align="center"
      />

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {HOME.whatWeDo.cards.map((card, index) => (
          // Staggered by 80ms, so the three read as one movement with some life
          // in it rather than three separate arrivals.
          <Reveal key={card.title} delay={index * 80}>
            <Card className="h-full">
              {/*
                **Title centred, paragraph left** — owner call, 2026-08-04.
                The heading is a label over a symmetrical card and sits centred
                like the section heading above it; the paragraph is five lines
                of prose, and centring that would give it a ragged left edge to
                find on every line.
              */}
              <h3 className="text-center text-h3">{card.title}</h3>
              <p className="mt-3 text-body leading-relaxed text-ink-700">{card.body}</p>
            </Card>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/** One icon per reason. Decorative, so `aria-hidden` — the heading carries the meaning. */
const WHY_ICONS = [Leaf, ShieldCheck, Users] as const;

function WhyChoose() {
  return (
    <Section tint="ink" labelledBy="why-choose">
      <SectionIntro id="why-choose" heading={HOME.whyChoose.heading} align="center" />

      <ul className="mt-12 grid gap-6 md:grid-cols-3">
        {HOME.whyChoose.items.map((item, index) => {
          const Icon = WHY_ICONS[index] ?? Leaf;
          return (
            <Reveal as="li" key={item.title} delay={index * 80}>
              <Card className="h-full text-center">
                <span className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-honey-100 text-bark-700">
                  <Icon className="size-5" aria-hidden />
                </span>
                <h3 className="mt-4 text-h3">{item.title}</h3>
                <p className="mt-2 text-body leading-relaxed text-ink-700">{item.body}</p>
              </Card>
            </Reveal>
          );
        })}
      </ul>
    </Section>
  );
}

function Awards() {
  return (
    <Section labelledBy="awards">
      <SectionIntro
        id="awards"
        eyebrow={HOME.awards.eyebrow}
        heading={HOME.awards.heading}
        lede={HOME.awards.body}
        align="center"
      />

      <div className="mx-auto mt-10 max-w-3xl">
        <Reveal className="flex flex-col items-center gap-6 rounded-2xl border border-honey-200 bg-honey-50 px-6 py-8 text-center sm:flex-row sm:gap-10 sm:px-10 sm:py-10 sm:text-left">
          <img
            src={FEATURED_AWARD.src}
            alt={awardAlt(FEATURED_AWARD.year)}
            width={224}
            height={224}
            loading="lazy"
            className="size-44 shrink-0 object-contain sm:size-56"
          />
          <div>
            <p className="text-label font-semibold uppercase tracking-widest text-bark-700">
              Our latest recognition
            </p>
            <h3 className="mt-3 text-[32px] font-bold leading-tight tracking-tight text-ink-900 sm:text-[40px]">
              2026 Gold Winner
            </h3>
            <p className="mt-2 text-[20px] font-medium text-ink-900">Housekeeping</p>
            <p className="mt-3 max-w-sm text-body leading-relaxed text-ink-700">
              Thank you, Iron County, for choosing Honeycomb for the fourth year in a row.
            </p>
          </div>
        </Reveal>

        <p className="mt-9 text-center text-meta font-medium text-ink-500">
          A tradition of care, year after year
        </p>
        <ul
          aria-label="Previous Best of Iron County awards"
          className="mx-auto mt-5 grid max-w-md grid-cols-3 items-center gap-5 sm:gap-10"
        >
          {AWARDS_HOME_ORDER.map((award, index) => (
            <Reveal as="li" key={award.year} delay={index * 80} className="min-w-0">
              <img
                src={award.src}
                alt={awardAlt(award.year)}
                width={112}
                height={112}
                loading="lazy"
                className="mx-auto aspect-square w-full max-w-28 object-contain"
              />
            </Reveal>
          ))}
        </ul>
      </div>
    </Section>
  );
}

/*
 * How it works — ROADMAP.md §8.11, owner-requested.
 *
 * **The only copy on this page that was written rather than ported**, approved
 * 2026-08-05 — and the business owner has still to read it. See
 * `HOME_HOW_IT_WORKS` in public-content.ts for why it exists at all and what
 * the wording is allowed to say. Everything else here is ported word for word.
 *
 * **The `honey-50` band with numbered circles is not a new idea** —
 * design-system.md §7 already specifies it, as the "numbered explainer" used on
 * Calendar Sync, and says to reuse it "for any feature with setup steps". So
 * this section borrows a pattern the design system already owns rather than
 * inventing a look for one page. It also puts a quiet tint between two
 * untinted sections, which is why it sits here and not beside `Crew`.
 *
 * **Centred**, per the rule `Section.tsx` encodes: a heading is aligned the way
 * the thing it introduces is aligned, and four evenly-spaced steps are as
 * symmetrical as a row gets. The bodies are centred too — two lines each,
 * closer to Why Choose's one-liners than to What We Do's five-line paragraphs,
 * which 8.9 deliberately left flush left.
 */
function HowItWorks() {
  return (
    <Section tint="honey" labelledBy="how-it-works">
      <SectionIntro
        id="how-it-works"
        eyebrow={HOME_HOW_IT_WORKS.eyebrow}
        heading={HOME_HOW_IT_WORKS.heading}
        lede={HOME_HOW_IT_WORKS.lede}
        align="center"
      />

      {/*
        An `<ol>`, because the order is the meaning — these are steps, not a set
        of features, and a screen reader should say so.

        `role="list"` is not redundant. Tailwind's preflight sets
        `list-style: none` on every list, and Safari drops list semantics
        entirely when it sees that — so without the explicit role this announces
        as four loose paragraphs and the sequence disappears for exactly the
        people who cannot see the numbers.
      */}
      <ol role="list" className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {HOME_HOW_IT_WORKS.steps.map((step, index) => (
          // 80ms apart, so four steps read as one movement rather than four
          // arrivals — and left to right, which is the order they are read in.
          <Reveal as="li" key={step.title} delay={index * 80} className="text-center">
            {/*
              Ink on honey — 14.75:1, and the only correct use of the brand
              yellow (design-system.md §3). `aria-hidden` because the `<ol>`
              already carries the ordering; announcing "1" and then "1 of 4"
              would say the same thing twice.
            */}
            <span
              aria-hidden
              className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-honey-400 text-[18px] font-bold text-ink-900 shadow-card"
            >
              {index + 1}
            </span>

            <h3 className="mt-4 text-h3">{step.title}</h3>
            {/* Held to about 34 characters a line, so four narrow columns wrap
                evenly instead of one of them going ragged. */}
            <p className="mx-auto mt-2 max-w-[34ch] text-body leading-relaxed text-ink-700">
              {step.body}
            </p>
          </Reveal>
        ))}
      </ol>
    </Section>
  );
}

function Crew() {
  return (
    <Section tint="ink" labelledBy="crew">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal className="lg:order-2">
          <Photo src={CREW_PHOTO.src} alt={CREW_PHOTO.alt} imageClassName="aspect-[4/3]" />
        </Reveal>

        <Reveal delay={80} className="lg:order-1">
          {/* Left, deliberately. This is prose somebody reads, and centred body
              copy has a ragged left edge that is measurably harder to follow. */}
          <SectionHeading id="crew">{HOME.crew.heading}</SectionHeading>
          {HOME.crew.body.map((paragraph) => (
            <Lede key={paragraph}>{paragraph}</Lede>
          ))}
          <Button asChild size="lg" className="mt-8">
            <Link to="/contact#quote">{HOME.crew.cta}</Link>
          </Button>
        </Reveal>
      </div>
    </Section>
  );
}

function OurWork() {
  return (
    <Section labelledBy="our-work">
      <SectionIntro
        id="our-work"
        eyebrow={HOME.work.eyebrow}
        heading={HOME.work.heading}
        align="center"
      />
      <Reveal>
        <WorkCarousel />
      </Reveal>
    </Section>
  );
}

function ClosingCta() {
  return (
    <section aria-labelledby="closing-cta" className="relative overflow-hidden bg-honey-400">
      {/*
        A second, quieter wash so the closing band is not one flat rectangle of
        yellow. Same reasoning as the hero: texture, not a new colour.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-honey-300/60 via-transparent to-honey-500/30"
      />

      <div className="relative mx-auto max-w-content px-4 py-20 text-center sm:px-6">
        <Reveal>
          {/*
            Yellow is a background and the text on it is `ink-900` — 14.75:1.
            design-system.md §3, and the one rule the prototype breaks three
            separate times.
          */}
          <h2
            id="closing-cta"
            className="text-balance text-[32px] font-bold text-ink-900 sm:text-[40px]"
          >
            {HOME.closing.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[18px] text-ink-900/80">{HOME.closing.body}</p>
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-ink-900 text-white shadow-card transition-shadow hover:bg-ink-700 hover:shadow-pop"
            >
              <a href={NAP.phoneHref}>
                <Phone aria-hidden />
                {NAP.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              className="border border-ink-900 bg-white text-ink-900 hover:bg-honey-50"
            >
              <Link to="/contact#quote">{HOME.closing.cta}</Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
